import {
    faArrowDownWideShort,
    faArrowUpShortWide,
    faCheck,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Input,
    Select,
    SelectItem,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { Key } from "@react-types/shared";

type MobileFilterDrawerProps = {
    isOpen: boolean;
    onClose: () => void;

    searchValue?: string;
    serviceProviderId?: string;
    growerId?: string;
    accountManagerId?: string;
    createdDate?: string;

    showServiceProviderFilter: boolean;
    showGrowerFilter: boolean;
    showAccountManagerFilter?: boolean;
    showRecentlyCreated: boolean;

    serviceProviderOptions: { key: string; label: string }[];
    growerOptions: { key: string; label: string }[];
    accountManagerOptions?: { key: string; label: string }[];

    ordering?: string;
    sortOptions: { key: string; label: string }[];

    onApply: (filters: {
        searchValue: string;
        serviceProviderId?: string;
        growerId?: string;
        accountManagerId?: string;
        recentlyCreated: boolean;
        sortKey: string;
        sortDesc: boolean;
    }) => void;

    onClear: () => void;
};

export default function MobileFilterDrawer({
    isOpen,
    onClose,
    searchValue,
    serviceProviderId,
    growerId,
    accountManagerId,
    createdDate,
    showServiceProviderFilter,
    showGrowerFilter,
    showAccountManagerFilter = false,
    showRecentlyCreated,
    serviceProviderOptions,
    growerOptions,
    accountManagerOptions = [],
    ordering,
    sortOptions,
    onApply,
    onClear,
}: MobileFilterDrawerProps) {
    const [localSearch, setLocalSearch] = useState(searchValue ?? "");
    const [localServiceProviderId, setLocalServiceProviderId] =
        useState<Key | null>(serviceProviderId ?? null);
    const [localGrowerId, setLocalGrowerId] = useState<Key | null>(
        growerId ?? null,
    );
    const [localAccountManagerId, setLocalAccountManagerId] =
        useState<Key | null>(accountManagerId ?? null);
    const [recentlyCreated, setRecentlyCreated] = useState(!!createdDate);

    const [localSortKey, setLocalSortKey] = useState<Key | null>(
        ordering ? ordering.replace(/^-/, "") : null,
    );
    const [localSortDesc, setLocalSortDesc] = useState(
        !!ordering && ordering.startsWith("-"),
    );

    useEffect(() => {
        if (!isOpen) return;

        setLocalSearch(searchValue ?? "");
        setLocalServiceProviderId(serviceProviderId ?? null);
        setLocalGrowerId(growerId ?? null);
        setLocalAccountManagerId(accountManagerId ?? null);
        setRecentlyCreated(!!createdDate);
        setLocalSortKey(ordering ? ordering.replace(/^-/, "") : null);
        setLocalSortDesc(!!ordering && ordering.startsWith("-"));
    }, [
        accountManagerId,
        createdDate,
        growerId,
        isOpen,
        ordering,
        searchValue,
        serviceProviderId,
    ]);

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            placement="bottom"
            size="md"
            scrollBehavior="inside"
        >
            <DrawerContent>
                <DrawerHeader>Filters</DrawerHeader>

                <DrawerBody className="flex flex-col gap-3">
                    <Input
                        label="Search"
                        placeholder="Search..."
                        size="sm"
                        value={localSearch}
                        onValueChange={setLocalSearch}
                        isClearable
                        onClear={() => setLocalSearch("")}
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                    />

                    {showServiceProviderFilter && (
                        <Autocomplete
                            label="Service Provider"
                            placeholder="Service Provider"
                            size="sm"
                            selectedKey={localServiceProviderId}
                            onSelectionChange={setLocalServiceProviderId}
                            allowsCustomValue={false}
                            isVirtualized
                        >
                            {serviceProviderOptions.map((option) => (
                                <AutocompleteItem
                                    key={option.key}
                                    textValue={option.label}
                                >
                                    {option.label}
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                    )}

                    {showGrowerFilter && (
                        <Autocomplete
                            label="Farm"
                            placeholder="Farm"
                            size="sm"
                            selectedKey={localGrowerId}
                            onSelectionChange={setLocalGrowerId}
                            allowsCustomValue={false}
                            isVirtualized
                        >
                            {growerOptions.map((option) => (
                                <AutocompleteItem
                                    key={option.key}
                                    textValue={option.label}
                                >
                                    {option.label}
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                    )}

                    {showAccountManagerFilter && (
                        <Autocomplete
                            label="Account Manager"
                            placeholder="Account Manager"
                            size="sm"
                            selectedKey={localAccountManagerId}
                            onSelectionChange={setLocalAccountManagerId}
                            allowsCustomValue={false}
                            isVirtualized
                        >
                            {accountManagerOptions.map((option) => (
                                <AutocompleteItem
                                    key={option.key}
                                    textValue={option.label}
                                >
                                    {option.label}
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                    )}

                    {showRecentlyCreated && (
                        <Button
                            size="sm"
                            color={recentlyCreated ? "primary" : "default"}
                            onPress={() => setRecentlyCreated((prev) => !prev)}
                            startContent={
                                recentlyCreated ? (
                                    <FontAwesomeIcon icon={faCheck} />
                                ) : null
                            }
                        >
                            Recently Created
                        </Button>
                    )}

                    {sortOptions.length > 0 && (
                        <div className="flex flex-row gap-2 items-end">
                            <Select
                                label="Sort by"
                                size="sm"
                                className="flex-1"
                                selectedKeys={
                                    localSortKey != null ? [localSortKey] : []
                                }
                                onSelectionChange={(keys) => {
                                    const first = Array.from(
                                        keys as Set<Key>,
                                    )[0];
                                    setLocalSortKey(first ?? null);
                                }}
                            >
                                {sortOptions.map((option) => (
                                    <SelectItem key={option.key}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </Select>
                            <Button
                                size="sm"
                                isIconOnly
                                variant="flat"
                                aria-label={
                                    localSortDesc ? "Descending" : "Ascending"
                                }
                                onPress={() =>
                                    setLocalSortDesc((prev) => !prev)
                                }
                            >
                                <FontAwesomeIcon
                                    icon={
                                        localSortDesc
                                            ? faArrowDownWideShort
                                            : faArrowUpShortWide
                                    }
                                />
                            </Button>
                        </div>
                    )}
                </DrawerBody>

                <DrawerFooter>
                    <Button
                        variant="faded"
                        onPress={() => {
                            setLocalSearch("");
                            setLocalServiceProviderId(null);
                            setLocalGrowerId(null);
                            setLocalAccountManagerId(null);
                            setRecentlyCreated(false);
                            onClear();
                            onClose();
                        }}
                    >
                        Clear All
                    </Button>

                    <Button
                        color="primary"
                        onPress={() => {
                            onApply({
                                searchValue: localSearch.trim(),
                                serviceProviderId: localServiceProviderId
                                    ? localServiceProviderId.toString()
                                    : undefined,
                                growerId: localGrowerId
                                    ? localGrowerId.toString()
                                    : undefined,
                                accountManagerId: localAccountManagerId
                                    ? localAccountManagerId.toString()
                                    : undefined,
                                recentlyCreated,
                                sortKey: localSortKey
                                    ? localSortKey.toString()
                                    : "",
                                sortDesc: localSortDesc,
                            });

                            onClose();
                        }}
                    >
                        Apply
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
